import { Button } from '@rocketlane/ui';
import { Space } from 'antd';
import { FC } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { Heading03, Short01 } from '../../styles';
// Migration blocker: the package link variant has fixed button height/padding;
// it cannot reproduce this compact, intrinsic-height secondary CTA.
import { LinkButton } from '../button/link-button';

interface ClassNameOnlyProps {
  className?: string;
}

const PrimaryCTA = styled(Button).attrs({
  variant: 'primary',
})``;

const SecondaryCTA = styled(LinkButton)`
  display: flex;
  align-items: center;
  text-align: left;
`;
const StyledCallToAction = styled(Space).attrs({
  direction: 'vertical',
  size: 16,
})``;

type CallToAction = typeof StyledCallToAction & {
  Primary: typeof PrimaryCTA;
  Secondary: typeof SecondaryCTA;
};

const CallToAction: CallToAction = StyledCallToAction as any;

CallToAction.Primary = PrimaryCTA;
CallToAction.Secondary = SecondaryCTA;

const Image = styled.div`
  display: flex;
  color: var(--icon-secondary);
`;

const Title = styled.div`
  ${Heading03};
  color: var(--text-tertiary);
`;

const Message = styled.div`
  ${Short01};
  color: var(--text-secondary);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-07);
`;
const Info = styled(Content)`
  gap: var(--spacing-03);
`;
const CTA = styled(Content)`
  gap: var(--spacing-05);
  flex-direction: row;
`;

const Container = styled.div`
  --empty-state-alignment: center;
  display: flex;
  flex-direction: column;
  justify-content: var(--empty-state-alignment);
  gap: var(--spacing-05);
  ${Image}, ${CTA} {
    justify-content: var(--empty-state-alignment);
  }
  ${Title}, ${Message} {
    display: inline-flex;
    justify-content: var(--empty-state-alignment);
  }
`;

interface EmptyStateComponentProps extends ClassNameOnlyProps {
  image?: React.ReactNode;
  title?: any;
  message?: React.ReactNode;
  primaryCTA?: React.ReactNode;
  secondaryCTA?: React.ReactNode;
}

const EmptyStateComponent: FC<EmptyStateComponentProps> = (props) => {
  const { className, image, title, message, primaryCTA, secondaryCTA } = props;

  return (
    <Container className={className}>
      {image && <Image>{image}</Image>}
      <Content>
        {(title || message) && (
          <Info>
            {title && <Title>{title}</Title>}
            {message && <Message>{message}</Message>}
          </Info>
        )}
        {(primaryCTA || secondaryCTA) && (
          <CTA>
            {primaryCTA}
            {secondaryCTA}
          </CTA>
        )}
      </Content>
    </Container>
  );
};

type EmptyState = typeof EmptyStateComponent & {
  Image: typeof Image;
  Title: typeof Title;
  Message: typeof Message;
  CallToAction: typeof CallToAction;
};

const EmptyState: EmptyState = EmptyStateComponent as any;

EmptyState.Image = Image;
EmptyState.Title = Title;
EmptyState.Message = Message;
EmptyState.CallToAction = CallToAction;

const EmptyStateContainer = styled.div<{ $transparent?: boolean }>`
  background-color: ${(props) =>
    props.$transparent ? 'transparent' : 'var(--white-100)'};
  padding: var(--spacing-09) var(--spacing-08);
  display: flex;
  justify-content: center;
`;

export { EmptyState, EmptyStateContainer };
